import { Injectable } from "@angular/core";
import { DataService } from "@app/core/services/data.service";
import {
  Platform,
  PlatformAccount,
  PlatformTypes,
  ProvisioningRetryGetDto,
  ProvisioningRetryPostDto,
  ProvisioningStatusGetDto,
  ProvisioningTargetDto,
  UserPlatformAccount
} from "@app/models";
import { ExternalFeature, PlatformService } from "clearline-api";
import { AppConfigService } from "core-kit";
import { Observable } from "rxjs";
import { map, mergeMap } from "rxjs/operators";

// todo: migrate API calls to PlatformService
@Injectable({ providedIn: "root" })
export class PlatformAccountService extends PlatformService {
  constructor(private http: DataService, private configSvc: AppConfigService) {
    super(http);
  }

  getPlatformAccountById(id: number): Observable<PlatformAccount> {
    return this.http.get(`${this.serviceUrl}/${id}`);
  }

  getAll(): Observable<Platform[]> {
    return this.http.get(`${this.serviceUrl}/allPlatforms`);
  }

  getPlatformAccounts(userId: number): Observable<UserPlatformAccount[]> {
    return this.http.get(`${this.serviceUrl}/userPlatformAccouns/${userId}`);
  }

  getLocationPlatformAccounts(locationId: number): Observable<PlatformAccount[]> {
    return this.http.get(`${this.serviceUrl}/platformAccouns/location/${locationId}`);
  }

  getBrandPlatformAccounts(brandId: number): Observable<PlatformAccount[]> {
    return this.http.get(`${this.serviceUrl}/brandPlatformAccouns/${brandId}`);
  }

  getAvaliableUserPlatforms(userId: number): Observable<Platform[]> {
    return this.getPlatformAccounts(userId).pipe(
      mergeMap((accounts) => {
        return this.getAll().pipe(map((platforms) => platforms.filter((p) => !accounts.some((ac) => ac.platformId === p.id))));
      })
    );
  }

  getAvaliableBrandPlatforms(brandId: number): Observable<Platform[]> {
    return this.getBrandPlatformAccounts(brandId).pipe(
      mergeMap((accounts) => {
        return this.getAll().pipe(map((platforms) => platforms.filter((p) => !accounts.some((ac) => ac.platformId === p.id))));
      })
    );
  }

  addPlatformAccount(account: PlatformAccount) {
    return this.http.post(`${this.serviceUrl}/addAccount`, account);
  }

  addBrandPlatformAccount(brandId: number, account: PlatformAccount) {
    return this.http.post(`${this.serviceUrl}/addBrandAccount/${brandId}`, account);
  }

  updatePlatformAccount(account: PlatformAccount) {
    return this.http.post(`${this.serviceUrl}/updateAccount`, account);
  }

  deletePlatformAccount(accountId: number) {
    return this.http.delete(`${this.serviceUrl}/deletePlatformAccount/${accountId}`);
  }

  getConfiguredExternalFeatures(): Observable<ExternalFeature[]> {
    return this.http.get(`${this.serviceUrl}/allPredefinedExternalFeatures`);
  }

  getConfiguredExternalFeaturesWithPlatform(): Observable<ExternalFeature[]> {
    return this.getConfiguredExternalFeatures().pipe(
      mergeMap((prs) =>
        this.getAll().pipe(
          map((pls) => {
            const plObj = {};
            pls.forEach((pl) => (plObj[pl.id] = pl));
            return prs.map((pr) => {
              pr.platform = plObj[pr.platformId];
              return pr;
            });
          })
        )
      )
    );
  }

  getProvisioningStatus(target: ProvisioningTargetDto): Observable<ProvisioningStatusGetDto> {
    return this.http.get(`${this.serviceUrl}/provisioningStatus`, target);
  }

  retryProvisioning(dto: ProvisioningRetryPostDto): Observable<ProvisioningRetryGetDto> {
    return this.http.post(`${this.serviceUrl}/provisioning/retry`, dto);
  }

  openPlatform(platformId: number) {
    let returnUrl = "";
    if (platformId === PlatformTypes.TwoReward) returnUrl = "https://login.2reward.me/index.php?pag=dashboard";
    if (platformId === PlatformTypes.SmsUSA) returnUrl = "https://login.clearlinemobile.com/responsive/messaging.asp";
    if (platformId === PlatformTypes.SmsGlobal) returnUrl = "http://sms.clearlinemobile.com/analyze/dashboard/";
    window.open(returnUrl);
  }

  getCouponExternalSetupUrl() {
    return `${this.configSvc.appData.identityUrl}/ExternalFeature/digitalcoupons`;
  }

  /** @deprecated */
  getExternalExternalFeatureUrlByCode(externalFeatureCode: string) {
    return `${this.configSvc.appData.identityUrl}/ExternalFeature/${externalFeatureCode}`;
  }
}
